import { AgentDesktopRouter } from '@agent/agent-interface/agent-desktop-router';
import { AgentDesktopState } from '@agent/agent-interface/agent-desktop-routing.module';
import { ModalService } from '@commons/core/ui/modal/modal.service';
import { Store } from '@ngrx/store';
import { ILogService } from 'angular';
import { i18n } from 'i18next';
import {
  catchError,
  debounceTime,
  filter,
  first,
  ignoreElements,
  interval,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

import {
  Action,
  AgentChannelFacade,
  AgentUserFacade,
  ChanEnum,
  ChannelType,
  Configuration,
  isCommonReady,
  isConfigLoaded,
  Option,
  ToasterFacade,
  waitToLoad,
} from '@contact/upgrade';

import { IAgentConfigDeprecatedService } from '../agent/agent-config-deprecated.service';
import { ContactHistoryService } from '../contact/history/contact-history.service';
import { BasketAction, fromBasket, MailBasketAction } from './';
import { Refresh } from './basket.action';
import { IBasketStore } from './basket.store';
import {
  changeBookmarkEffect,
  loadBasketEffect,
  loadGlobalEffect,
  openRequestEffect,
  resetFilterEffect,
  saveFilterEffect,
  saveSortEffect,
} from './mail/mail-basket.effect';
import { INITIAL_STATE as MAIL_INITIAL_STATE } from './mail/mail-basket.model';
import { IMailBasketService } from './mail/mail-basket.service';
import { SW } from './models/basket';
import { loadBasketEffect as loadBasketVisioEffect, loadGlobalEffect as loadGlobalVisioEffect } from './visio/visio-basket.effect';
import { INITIAL_STATE as visioInitialState } from './visio/visio-basket.model';
import { IVisioBasketService } from './visio/visio-basket.service';

const MODULE_NAME = 'basketEffect';

export function intializeEffect({ store$, agentChannelFacade, agentUserFacade, mailBasketService, toasterFacade }: SW): Observable<Action> {
  return store$.pipe(
    waitToLoad([isCommonReady, isConfigLoaded]),
    withLatestFrom(agentChannelFacade.has(ChanEnum.In.Mail), agentChannelFacade.has(ChanEnum.Out.Visio), agentUserFacade.config$),
    switchMap(([_, mail, visio, config]) => {
      if (mail) {
        return mailBasketService.getFiltersAndSort().pipe(
          map(result => ({
            mail,
            visio,
            config,
            mailInitialState: mailBasketService.overrideMailInitialState(MAIL_INITIAL_STATE, result),
          })),
          catchError(() => {
            toasterFacade.alert({ message: 'agent:toast.mail_filter_load_failed' });
            return of({
              mail,
              visio,
              config,
              mailInitialState: MAIL_INITIAL_STATE,
            });
          })
        );
      }
      return of({
        mail,
        visio,
        config,
        mailInitialState: MAIL_INITIAL_STATE,
      });
    }),
    mergeMap(({ mail, visio, config, mailInitialState }) => {
      const actions: Action[] = [];
      let channel: ChannelType | undefined = undefined;
      if (mail) {
        actions.push(new BasketAction.AddChannel(mailInitialState));
        actions.push(new MailBasketAction.SetPageSize(ChannelType.MAIL, config.mail.maxMailBasketByPage));
        channel = channel || ChannelType.MAIL;
      }
      if (visio) {
        actions.push(new BasketAction.AddChannel(visioInitialState));
        channel = channel || ChannelType.VISIO;
      }
      if (channel) {
        actions.push(new BasketAction.ChangeChannel(channel));
      }
      return actions;
    })
  );
}

/**
 * Refresh effect
 * when store receive a manual refresh action we restart interval
 * on each interval we send a load action in store
 */
export function refreshEffect({ basketStore }: SW, timerRequestInMillis: number): Observable<Action> {
  return merge(
    // to first initialize interval
    of(true),
    // on each refresh action
    basketStore.actions$.pipe(
      filter<Refresh>(action => action.type === BasketAction.ActionTypes.REFRESH),
      map(action => action.refreshType === 'auto')
    ),
    // on each change page action
    basketStore.actions$.pipe(
      filter(action => action.type === BasketAction.ActionTypes.CHANGE_PAGE),
      map(() => false)
    )
  ).pipe(
    // cancel last interval and restart new
    switchMap(autoRefresh =>
      merge(
        // to emit immediate event
        of(autoRefresh),
        // emit on each interval
        interval(timerRequestInMillis).pipe(map(() => true))
      )
    ),
    // ignore speed multi emitting event
    debounceTime(500),
    // get store data
    withLatestFrom(basketStore.pipe(fromBasket.selectChannelSelected()), basketStore.pipe(fromBasket.selectLabelSelected())),
    // dispatch channel/label typed load action
    map(([autoRefresh, channel, label]) => new BasketAction.Load(channel, label, autoRefresh))
  );
}

/**
 * conditional refresh action effect
 */
export function navigationRefreshEffect({ basketStore }: SW, cacheRequestInMillis: number): Observable<Action> {
  return basketStore.actions$.pipe(
    // on each open basket action
    // on each change category action
    // on each change label action
    // on each change channel action
    filter(
      action =>
        action.type === BasketAction.ActionTypes.OPEN_BASKET ||
        action.type === BasketAction.ActionTypes.CHANGE_CATEGORY ||
        action.type === BasketAction.ActionTypes.CHANGE_LABEL ||
        action.type === BasketAction.ActionTypes.CHANGE_CHANNEL
    ),
    // get store data
    withLatestFrom(basketStore.pipe(fromBasket.selectCurrentTs()), (_, ts) => ts),
    // if last loading request time is greater to max timer configuration
    filter(ts => ts === 0 || new Date().getTime() - ts > cacheRequestInMillis),
    // force refresh
    map(() => new BasketAction.NavigationRefresh())
  );
}

/**
 * open basket effect
 */
export function openBasketEffect({ basketStore, router }: SW): Observable<Action> {
  return basketStore.actions$.pipe(
    // on each open basket action
    filter(action => action.type === BasketAction.ActionTypes.OPEN_BASKET),
    withLatestFrom(router.currentUrl$, (_, url) => {
      return url;
    }),
    filter(url => url?.indexOf(AgentDesktopState.BASKET) === -1),
    // open ui state basket
    tap(() => router.navigateToTile([AgentDesktopState.BASKET])),
    // don't resend action
    ignoreElements()
  );
}

/**
 * reset cache effect
 */
export function resetCacheEffect({ basketStore }: SW): Observable<Action> {
  return basketStore.actions$.pipe(
    // on each refresh action
    filter<Refresh>(action => action.type === BasketAction.ActionTypes.REFRESH),
    // if manual refresh
    filter<Refresh>(action => action.refreshType === 'manual'),
    // reset cache basket
    map(() => new BasketAction.ResetCache())
  );
}

export interface IBasketEffect {}

export class BasketEffect implements IBasketEffect {
  static $inject: Array<string> = [
    'basketStore',
    '$log',
    'agentDesktopRouter',
    '$i18next',
    'configuration',
    'agentConfigDeprecatedService',
    'mailBasketService',
    'visioBasketService',
    'toasterFacade',
    'ngrxStoreService',
    'agentUserFacade',
    'agentChannelFacade',
    'modalService',
    'agentHistory',
  ];

  public name = MODULE_NAME;

  constructor(
    // only inject service use for effect
    private readonly basketStore: IBasketStore,
    private readonly $log: ILogService,
    private readonly router: AgentDesktopRouter,
    private readonly $i18next: i18n,
    private readonly configuration: Configuration,
    private readonly agentConfigDeprecatedService: IAgentConfigDeprecatedService,
    private readonly mailBasketService: IMailBasketService,
    private readonly visioBasketService: IVisioBasketService,
    private readonly toasterFacade: ToasterFacade,
    private readonly store$: Store,
    private readonly agentUserFacade: AgentUserFacade,
    private readonly agentChannelFacade: AgentChannelFacade,
    private readonly modalService: ModalService,
    private readonly contactHistoryService: ContactHistoryService
  ) {
    let timerRequestInMillis = Option.of(this.configuration.basket?.timerRequestInMillis).getOrElse(30000);
    let cacheRequestInMillis = Option.of(this.configuration.basket?.cacheRequestInMillis).getOrElse(30000);
    let sw: SW = {
      basketStore: this.basketStore,
      router: this.router,
      $i18next: this.$i18next,
      agentConfigDeprecatedService: this.agentConfigDeprecatedService,
      mailBasketService: this.mailBasketService,
      visioBasketService: this.visioBasketService,
      toasterFacade: this.toasterFacade,
      store$: this.store$,
      agentUserFacade: this.agentUserFacade,
      agentChannelFacade: this.agentChannelFacade,
      modalService: this.modalService,
      contactHistoryService: this.contactHistoryService,
    };

    merge(
      ...this.safeEffect([
        // BASKET
        intializeEffect(sw),
        refreshEffect(sw, timerRequestInMillis),
        navigationRefreshEffect(sw, cacheRequestInMillis),
        openBasketEffect(sw),
        resetCacheEffect(sw),
      ])
    ).subscribe(action => this.basketStore.dispatch(action));

    const mailEffect$ = merge(
      ...this.safeEffect([
        // MAIL
        loadBasketEffect(sw),
        loadGlobalEffect(sw),
        openRequestEffect(sw),
        saveFilterEffect(sw),
        resetFilterEffect(sw),
        changeBookmarkEffect(sw),
        saveSortEffect(sw),
      ])
    );

    this.store$
      .pipe(
        // wait agent configuration be loaded
        waitToLoad([isConfigLoaded]),
        // check if agent has mail in/out channel
        switchMap(() => this.agentChannelFacade.has(ChanEnum.In.Mail)),
        first(hasMail => hasMail),
        switchMap(() => mailEffect$)
      )
      .subscribe(action => this.basketStore.dispatch(action));

    const visioEffect$ = merge(...this.safeEffect([loadBasketVisioEffect(sw), loadGlobalVisioEffect(sw)]));

    this.store$
      .pipe(
        // wait agent configuration be loaded
        waitToLoad([isConfigLoaded]),
        // check if agent has mail in/out channel
        switchMap(() => this.agentChannelFacade.has(ChanEnum.Out.Visio)),
        first(hasVisio => hasVisio),
        switchMap(() => visioEffect$)
      )
      .subscribe(action => this.basketStore.dispatch(action));
  }

  private safeEffect(effects: Observable<Action>[]): Observable<Action>[] {
    return effects.reduce((result, effect$) => [...result, this.autoResubEffect(effect$)], []);
  }

  private autoResubEffect(effect$: Observable<Action>, i: number = 0) {
    return effect$.pipe(
      catchError(err => {
        if (i++ > 1) {
          this.$log.error(`Recursive resubscription error cause probably memory leak (error: ${JSON.stringify(err)})`);
        }
        return this.autoResubEffect(effect$, i);
      })
    );
  }
}

export default MODULE_NAME;
